/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import CallToActionOutlined from '@mui/icons-material/CallToActionOutlined';
import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CrisisAlertOutlined from '@mui/icons-material/CrisisAlertOutlined';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import DesktopWindowsOutlined from '@mui/icons-material/DesktopWindowsOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { Avatar, Box, Divider, MenuList, Stack, Typography } from '@mui/material';
import { useUserPreferences } from '../desktop/preferences/UserPreferencesContext';
import { SegmentedControl } from '../desktop/settings/components/SegmentedControl';
import { formatSettingsShortcut } from '../desktop/settings/settingsShortcut';
import type { SettingsTabId } from '../desktop/settings/useOpenSettings';
import { DOCS_URL, getSupportTicketUrl } from '../support';
import { UserMenuItem } from './UserMenuItem';

const dividerSx = { mx: 1.5, my: 0.75 } as const;

/** Contents of the avatar menu: identity, quick theme switch, settings, help and sign out. */
export const UserMenuPanel = ({
  userName,
  userEmail,
  userInitials,
  showSystemAlerts,
  onOpenSettings,
  onOpenPath,
  onClose,
  onLogout,
}: Readonly<{
  userName: string;
  userEmail?: string;
  userInitials: string;
  showSystemAlerts: boolean;
  onOpenSettings: (tab: SettingsTabId) => void;
  onOpenPath: (path: string) => void;
  onClose: () => void;
  onLogout: () => void;
}>) => {
  const { preferences, setPreference } = useUserPreferences();
  const openDocs = () => {
    onClose();
    window.open(DOCS_URL, '_blank', 'noopener,noreferrer');
  };
  // A mailto: link hands off to the mail client without opening a window.
  const contactSupport = () => {
    onClose();
    window.location.assign(getSupportTicketUrl());
  };
  return (
    <Stack sx={{ p: 0.75 }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', px: 1.25, pt: 1.25, pb: 1.5 }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            fontSize: 14,
            fontWeight: 700,
            color: '#0B1020',
            backgroundColor: '#FDE68A',
          }}
        >
          {userInitials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
            {userName}
          </Typography>
          {userEmail && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
              {userEmail}
            </Typography>
          )}
        </Box>
      </Stack>
      <Divider sx={dividerSx} />
      <Stack spacing={1} sx={{ px: 1.25, py: 1 }}>
        <Typography
          id="user-menu-theme"
          sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}
        >
          Theme
        </Typography>
        <SegmentedControl
          fullWidth
          aria-labelledby="user-menu-theme"
          value={preferences.appearance.theme}
          onChange={(value) => setPreference('appearance.theme', value)}
          options={[
            { value: 'light', label: 'Light', icon: <LightModeOutlined /> },
            { value: 'dark', label: 'Dark', icon: <DarkModeOutlined /> },
            { value: 'system', label: 'System', icon: <DesktopWindowsOutlined /> },
          ]}
        />
      </Stack>
      <Divider sx={dividerSx} />
      <MenuList aria-label="Account" disablePadding>
        <UserMenuItem
          icon={<SettingsOutlined />}
          label="Settings"
          shortcut={formatSettingsShortcut()}
          onClick={() => onOpenSettings('appearance')}
        />
        <UserMenuItem
          icon={<CallToActionOutlined />}
          label="Customize dock…"
          onClick={() => onOpenSettings('apps')}
        />
        <Divider sx={dividerSx} />
        <UserMenuItem icon={<MenuBookOutlined />} label="Help & docs" onClick={openDocs} />
        <UserMenuItem
          icon={<ChatBubbleOutlineOutlined />}
          label="Contact support"
          onClick={contactSupport}
        />
        {showSystemAlerts && <Divider sx={dividerSx} />}
        {showSystemAlerts && (
          <UserMenuItem
            icon={<CrisisAlertOutlined />}
            label="System Alerts"
            badge="Admin"
            onClick={() => onOpenPath('/admin/system-issues')}
          />
        )}
        <Divider sx={dividerSx} />
        <UserMenuItem icon={<LogoutOutlined />} label="Sign out" danger onClick={onLogout} />
      </MenuList>
    </Stack>
  );
};
